<template>
  <v-dialog v-model="dialog" fullscreen persistent>
    <v-container fluid class="py-0 fill-height">
      <v-row class="fill-height">
        <v-col cols="12" class="px-0 py-0">
          <v-card height="100%">
            <v-toolbar
              color="indigo darken-2"
              dark>
              <v-toolbar-title>
                <v-icon left>{{ filesIcon }}</v-icon>
                <template>
                    <span>{{
                        $vuetify.lang.t('$vuetify.map.requests.files')
                      }}</span>
                </template>
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon class="text-right" @click="$emit('close-requests-datatable')">
                <v-icon>{{ closeIcon }}</v-icon>
              </v-btn>
            </v-toolbar>

            <v-progress-linear :active="tableLoading" indeterminate absolute></v-progress-linear>

            <v-card-text class="my-0 py-0">

              <v-card class="my-5">
                <v-card-title class="my-0 py-0">{{
                    $vuetify.lang.t('$vuetify.map.requests.dataIterator.filters')
                  }}
                </v-card-title>
                <v-card-text class="my-0 py-0">
                  <v-divider></v-divider>
                  <v-row no-gutters justify="space-between">
                    <v-col cols="12" md="7">
                      <span class="subtitle-2">{{ $vuetify.lang.t('$vuetify.map.requests.dataIterator.status') }}</span>
                      <v-row no-gutters>
                        <v-col v-for="category in Object.values(ClientFileStatus)" :key="`${category}-switch`"
                               class="d-flex justify-center mx-2">
                          <v-switch
                            v-model="selectedStatuses[category]"
                            :label="`${$vuetify.lang.t(`$vuetify.map.requests.dataIterator.states.${category}`)}`"></v-switch>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12" md="5">
                      <span class="subtitle-2">{{ $vuetify.lang.t('$vuetify.map.requests.dataIterator.type') }}</span>
                      <v-row no-gutters>
                        <v-col v-for="category in Object.values(ClientFileType)" :key="`${category}-switch`"
                               class="d-flex justify-center">
                          <v-switch
                            v-model="selectedTypes[category]"
                            :label="`${$vuetify.lang.t(`$vuetify.map.requests.dataIterator.types.${category}`)}`"></v-switch>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-divider></v-divider>
                  <v-row no-gutters justify="space-between">
                    <span class="subtitle-2">{{
                        $vuetify.lang.t('$vuetify.map.requests.dataIterator.innerType')
                      }}</span>
                    <v-row no-gutters>
                      <v-col v-for="category in Object.values(ClientFileInnerType)"
                             :key="`$inner-type-${category}-switch`"
                             class="d-flex justify-center"
                              md="3" cols="12">
                        <v-switch
                          v-model="selectedInnerTypes[category]"
                          :label="`${$vuetify.lang.t(`$vuetify.map.requests.dataIterator.categories.${category}`)}`"></v-switch>
                      </v-col>
                    </v-row>
                  </v-row>
                  <v-divider></v-divider>
                  <v-row no-gutters>
                    <v-col>
                      <span class="subtitle-2">
                        {{ $vuetify.lang.t('$vuetify.map.requests.dataIterator.time') }}
                      </span>
                      <v-row no-gutters>
                        <v-col>
                          <datetime-chart-filter class="ml-md-n4"
                                                 v-if="timeRangeFilter.filter"
                                                 :is-time-first="true"
                                                 :time-first-row="true"
                                                 :start-date="`${timeRangeFilter.filter.date.start.date}`"
                                                 :finish-date="`${timeRangeFilter.filter.date.finish.date}`"
                                                 :trim-date="false"
                                                 :reset="true"
                                                 :non-card="true"
                                                 :header="$vuetify.lang.t('$vuetify.map.managerMap.localFilters.filters.timeRangeFilter.title')"
                                                 :is-active="true" :active-value="timeRangeFilter.isUsed"
                                                 @active-value-changed="timeRangeFilter.isUsed = $event"
                                                 @datetime-cleared="clearTimeRangeFilter"
                                                 @datetime-changed="setSlidesDatetime"></datetime-chart-filter>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="my-2">
                    <v-col>
                      <v-text-field class="mb-3 mt-n5 pt-0 pb-2"
                                    v-model="search"
                                    append-icon="mdi-magnify"
                                    single-line
                                    hide-details
                                    clearable
                                    :clear-icon="clearIcon"
                                    :label="$vuetify.lang.t('$vuetify.map.requests.dataIterator.search')"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions class="py-0 my-0">
                  <v-spacer></v-spacer>
                  <v-btn text color="green" @click="getFiles">
                    {{ $vuetify.lang.t('$vuetify.shared.apply') }}
                  </v-btn>
                </v-card-actions>
              </v-card>

              <v-data-table :headers="headers" :items="files" :options.sync="options" :loading="tableLoading"
                            :server-items-length="totalFiles" item-key="id" :mobile-breakpoint="0"
                            multi-sort
                            style="height:auto;" :footer-props="footerProps" class="elevation-1">

                <template v-for="item in iconedHeaders" v-slot:[`header.${item}`]="{header}">
                  <span :key="item" class="d-inline-flex align-center justify-start"
                        style="vertical-align: middle; word-wrap: break-word">
                    <v-icon left>{{ header.icon }}</v-icon>
                    <span>{{ header.text }}</span>
                  </span>
                </template>

                <template v-slot:item.file_timestamp="{item}">
                  {{ travelDateLocalized(item.file_timestamp) }}
                </template>

                <template v-slot:item.file_size="{item}">
                  {{ formatBytes(item.file_size) }}
                </template>

                <template v-slot:item.file_inner_type="{item}">
                  {{ $vuetify.lang.t(`$vuetify.map.requests.dataIterator.categories.${item.file_inner_type}`) }}
                </template>

                <template v-slot:item.status="{item}">
                  {{ $vuetify.lang.t(`$vuetify.map.requests.dataIterator.states.${item.status}`) }}
                </template>

                <template v-slot:item.username="{item}">
                  <v-avatar left v-if="item.avatar">
                    <v-img :src="item.avatar"></v-img>
                  </v-avatar>
                  {{ item.username }}
                </template>

                <template v-slot:item.actions="{item}">
                  <tooltip-basic v-if="item.status === ClientFileStatus.INCOMPLETE"
                                 :text="'$vuetify.map.requests.requestFile'"
                                 :icon="requestIcon"
                                 :emit-state="`request-client-file`"
                                 @request-client-file="requestClientFile(item)">
                  </tooltip-basic>
                  <tooltip-basic
                    v-if="item.status === ClientFileStatus.SAVED && isFileViewable(item.file_name)"
                    :text="'$vuetify.map.requests.showFile'"
                    :icon="showIcon"
                    :emit-state="`show-client-file`"
                    @show-client-file="showClientFile(item)"
                  ></tooltip-basic>
                  <tooltip-basic v-if="item.status === ClientFileStatus.SAVED"
                                 :text="'$vuetify.map.requests.downloadFile'"
                                 :icon="downloadIcon"
                                 :emit-state="`download-client-file`"
                                 @download-client-file="downloadClientFile(item)"
                  ></tooltip-basic>
                  <file-timeline v-if="item.history.length > 0" :history="item.history"></file-timeline>
                </template>

              </v-data-table>
            </v-card-text>

          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <client-file-viewer :dialog="clientFileViewerDialog" :entry="selectedEntry"
                        @close-client-file-viewer-dialog="clientFileViewerDialog = false; selectedEntry = null">
    </client-file-viewer>

  </v-dialog>
</template>

<script>
import {
  mdiAccount, mdiBullhorn,
  mdiClock,
  mdiClose, mdiDelete, mdiDownload, mdiEye,
  mdiFile,
  mdiFileMultiple,
  mdiFormatListBulletedSquare,
  mdiShape, mdiShapePlus,
  mdiWeight
} from '@mdi/js'
import backend from '@/backend'
import { defineTableHeader } from '@/utils/tableManager'
import { formatBytes, travelDateLocalized } from '@/utils/travels'
import { ClientFileInnerType, ClientFileStatus, ClientFileType, ServerRequestType } from '@/enum'
import FileTimeline from '@/components/map/serverRequests/fileTimeline'
import { FileTimelineEntry } from '@/models/serverRequests/FileTimelineEntry'
import { getCurrentTime } from '@/utils/time'
import { defaultChartDatetimeState } from '@/utils/filters'
import DatetimeChartFilter from '@/components/charts/utils/datetimeChartFilter'
import { ServerRequest } from '@/models/serverRequests/ServerRequest'
import { prepareEnumSwitches, requestFilesUrl, stripEnumSwitches } from '@/utils/requests'
import ClientFileViewer from '@/components/map/serverRequests/clientFileViewer'
import TooltipBasic from '@/components/utils/tooltipBasic'
import { isFileViewable, ServerFile } from '@/models/serverRequests/ServerFile'
import { getCurrentTimezoneForBackend } from '@/utils/timezone'

const defaultFilterState = () => ({
  fileName: {
    type: 'text',
    value: '',
    specialOnly: true,
    header: '$vuetify.map.managerMap.filterHeaders.fileName'
  }
})

const { _currentYear, _currentMonth, _lastDay } = getCurrentTime()

const getDefaultTimeRangeFilter = () => ({
  start: defaultChartDatetimeState(`${_currentYear}-${_currentMonth}-01`),
  finish: defaultChartDatetimeState(`${_currentYear}-${_currentMonth}-${_lastDay}`)
})

const entryToServerFile = (entry) => new ServerFile({
  id: entry.id,
  userId: entry.user_id,
  status: entry.status,
  fileTimestamp: entry.file_timestamp,
  fileName: entry.file_name,
  fileType: entry.file_type,
  fileSize: entry.file_size,
  history: entry.history
})

export default {
  name: 'requestsDatable',
  components: { TooltipBasic, ClientFileViewer, DatetimeChartFilter, FileTimeline },
  props: {
    dialog: Boolean
  },
  data: () => ({
    files: [],
    totalFiles: 0,
    options: {
      sortBy: ['file_timestamp'],
      sortDesc: [true],
      itemsPerPage: 10
    },

    selectedStatuses: prepareEnumSwitches(ClientFileStatus),
    selectedTypes: prepareEnumSwitches(ClientFileType),
    selectedInnerTypes: prepareEnumSwitches(ClientFileInnerType),
    timeRangeFilter: {
      isUsed: false,
      filter: {
        date: getDefaultTimeRangeFilter()
      }
    },
    search: null,

    filters: defaultFilterState(),
    footerProps: { 'items-per-page-options': [5, 10, 25, 50, -1] }, // if add more - page overflow can be reached!
    tableLoading: false,

    clientFileViewerDialog: false,
    selectedEntry: null,

    closeIcon: mdiClose,
    filesIcon: mdiFileMultiple,
    requestIcon: mdiBullhorn,
    showIcon: mdiEye,
    downloadIcon: mdiDownload,
    clearIcon: mdiDelete
  }),
  computed: {
    headers () {
      return [
        defineTableHeader('$vuetify.map.requests.dataIterator.name', 'file_name', mdiFile),
        defineTableHeader('$vuetify.map.managerMap.headers.username', 'username', mdiAccount),
        defineTableHeader('$vuetify.map.requests.dataIterator.type', 'file_type', mdiShape),
        defineTableHeader('$vuetify.map.requests.dataIterator.innerType', 'file_inner_type', mdiShapePlus),
        defineTableHeader('$vuetify.map.requests.dataIterator.time', 'file_timestamp', mdiClock),
        defineTableHeader('$vuetify.map.requests.dataIterator.size', 'file_size', mdiWeight),
        defineTableHeader('$vuetify.map.requests.dataIterator.status', 'status', mdiFormatListBulletedSquare),
        defineTableHeader('$vuetify.map.requests.dataIterator.actions', 'actions', null, false, '')
      ]
    },
    iconedHeaders () {
      return this.headers.filter(header => header.hasOwnProperty('icon')).map(header => header.value)
    }
  },
  methods: {
    async getFiles () {
      this.tableLoading = true
      try {
        const conditions = {
          allowedStatuses: stripEnumSwitches(this.selectedStatuses),
          allowedTypes: stripEnumSwitches(this.selectedTypes),
          allowedInnerTypes: stripEnumSwitches(this.selectedInnerTypes)
        }

        if (this.timeRangeFilter.isUsed) {
          conditions.timeRange = {
            ...this.timeRangeFilter.filter,
            timezone: getCurrentTimezoneForBackend()
          }
        }

        if (this.search !== null && this.search !== '') {
          conditions.search = this.search
        }

        const result = await backend.fetchRequestsDatatable({
          ...this.options,
          conditions,
          filters: this.filters
        })

        if ('data' in result && result.data) {
          for (let entry of result.data.items) {
            entry.history = entry.history.map(item => new FileTimelineEntry(item))
          }

          this.files = result.data.items
          this.totalFiles = result.data.total
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.tableLoading = false
      }
    },
    setSlidesDatetime (datetime) {
      this.timeRangeFilter.filter.date[datetime.type][datetime.mode] = datetime.value
    },
    clearTimeRangeFilter () {
      if (!this.timeRangeFilter.filter) {
        this.timeRangeFilter.filter = {
          date: null
        }
      }

      this.timeRangeFilter.filter.date = getDefaultTimeRangeFilter()
    },
    async requestClientFile (entry) {
      const fileRequest = entryToServerFile(entry)

      const data = new ServerRequest({
        requestType: ServerRequestType.FILE_UPLOAD,
        userId: fileRequest.userId,
        fileType: fileRequest.fileType,
        fileTimestamp: fileRequest.fileTimestamp.getTime(),
        fileName: fileRequest.fileName
      }).backendModel()

      try {
        await backend.sendServerRequest(data)
        entry.status = ClientFileStatus.REQUESTED

        entry.history.push(new FileTimelineEntry({
          id: `fake-${entry.id}`,
          fileId: entry.id,
          timestamp: null,
          status: ClientFileStatus.REQUESTED
        }))
      } catch (e) {
        console.error(e)
      }
    },
    async downloadClientFile (entry) {
      const result = await backend._apiGet({ url: requestFilesUrl(entryToServerFile(entry)), type: 'download' })

      const url = window.URL.createObjectURL(new Blob([result.data], { type: result.headers['content-type'] }))
      const link = document.createElement('a')
      link.href = url

      link.setAttribute('download', result.headers.filename)
      link.click()
      window.URL.revokeObjectURL(url)
    },
    showClientFile (entry) {
      this.selectedEntry = entryToServerFile(entry)
      this.clientFileViewerDialog = true
    }
  },
  watch: {
    options: {
      async handler () {
        // we don't use mount for initial loading because first data loading occurs here
        await this.getFiles()
      },
      deep: true
    },
    dialog: {
      async handler (value) {
        if (value) {
          await this.getFiles()
        }
      },
      immediate: true
    }
  },
  created () {
    this['travelDateLocalized'] = travelDateLocalized
    this['formatBytes'] = formatBytes
    this['isFileViewable'] = isFileViewable
    this['ClientFileStatus'] = ClientFileStatus
    this['ClientFileType'] = ClientFileType
    this['ClientFileInnerType'] = ClientFileInnerType
  }
}
</script>

<style scoped>

</style>
